import { useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import { useAllRuns } from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import { isLead, useRunPhase } from '@/kinds/agentrun/components'
import { isOver } from '@/kinds/agentrun/schema'
import { stepNow, stepOf } from '@/kinds/agentrun/toolSteps'
import type { AgentRunResource } from '@/kinds/agentrun'
import { useStream, type LiveLine } from '@/state/stream'
import { useRunStatus } from '@/components/RunStatus'
import { cx } from '@/components/ui'

// One live line per agent, in the sidebar (desk) and the launcher (phone): what it
// is doing now, read from the tool lines the thread already streams (`runLines`)
// and the mirror's own, through stepOf (toolSteps.ts). A row is a fixed height and
// truncates; the set of rows only changes when an agent starts or leaves, never as
// one streams, and a change of state swaps the words and the dot, nothing else.

/** Most rows: Prime and the newest few agents. */
const MAX_ROWS = 5
/** An ended agent keeps its "done" row this long. */
const KEEP_DONE_MS = 6 * 3600_000

const lastTool = (lines: readonly { role: string; text: string }[] | undefined) => {
  for (let i = (lines?.length ?? 0) - 1; i >= 0; i--) if (lines![i]!.role === 'tool') return lines![i]!.text
  return undefined
}

/** The newest tool line a run has, streamed or stored. A string, so a subscriber only re-renders when it changes. */
const useLastTool = (run: AgentRunResource) =>
  useStream((s) => lastTool(s.runLines[run.spec.name ?? ''] as LiveLine[] | undefined)) ??
  lastTool(run.spec.messages)

const useRows = () => {
  const runs = useAllRuns()
  const now = useNow(60_000)
  return useMemo(() => {
    const live = (runs ?? []).filter((r) => !r.deleted_at && r.spec.source.provider === 'factory')
    const lead = live.find((r) => isLead(r) && !isOver(r.spec.status))
    const cutoff = new Date(now - KEEP_DONE_MS).toISOString()
    const agents = live
      .filter((r) => !isLead(r) && (!isOver(r.spec.status) || r.spec.updated_at >= cutoff))
      // by when they started, so a state change never reorders them
      .sort(
        (a, b) =>
          (b.spec.started_at ?? '').localeCompare(a.spec.started_at ?? '') || a.id.localeCompare(b.id),
      )
    return [...(lead ? [lead] : []), ...agents].slice(0, MAX_ROWS)
  }, [runs, now])
}

const AgentLine = ({
  run,
  collapsed,
  onPick,
}: {
  run: AgentRunResource
  collapsed: boolean
  onPick?: () => void
}) => {
  const phase = useRunPhase(run)
  const statusOf = useRunStatus()
  const tool = useLastTool(run)
  const lead = isLead(run)
  const status = statusOf(run, phase)
  const working = phase === 'Working'
  const yours = status.holder === 'you'
  const words = working
    ? tool
      ? stepNow(stepOf(tool))
      : 'working'
    : phase === 'Starting' || phase === 'Pending'
      ? 'starting up'
      : phase === 'Ended'
        ? 'done'
        : status.word.toLowerCase()
  const title = lead ? 'Prime' : run.spec.title || run.spec.name || 'Agent'
  const dot = (
    <span
      className={cx(
        'h-2 w-2 rounded-full shrink-0',
        working || phase === 'Starting' || phase === 'Pending'
          ? 'bg-accent ship-pulse'
          : yours
            ? 'bg-accent'
            : 'border border-faint',
      )}
      aria-hidden
    />
  )
  return (
    <Link
      to="/agents/run/$id"
      params={{ id: run.id }}
      onClick={onPick}
      title={`${title}: ${words}`}
      aria-label={`${title}, ${words}`}
      className={cx(
        'rounded-md flex items-center hover:bg-raised/45',
        collapsed ? 'h-9 w-9 justify-center' : 'h-11 gap-2.5 px-2',
      )}
      data-testid="agent-now-row"
      data-state={working ? 'working' : 'still'}
    >
      {collapsed ? (
        dot
      ) : (
        <>
          <span className="w-4 flex justify-center shrink-0">{dot}</span>
          <span className="min-w-0 flex-1 flex flex-col justify-center leading-tight">
            <span className="truncate text-sm">{title}</span>
            <span className="truncate text-xs text-muted" data-testid="agent-now-words">
              {words}
            </span>
          </span>
        </>
      )}
    </Link>
  )
}

/** The agents' live lines. Nothing at all until there is an agent to show. */
export const AgentsNow = ({
  collapsed = false,
  label = true,
  onPick,
}: {
  collapsed?: boolean
  label?: boolean
  onPick?: () => void
}) => {
  const rows = useRows()
  if (rows.length === 0) return null
  return (
    <div
      className={cx('flex flex-col gap-px', collapsed && 'items-center')}
      data-testid="agents-now"
      aria-label="Agents"
    >
      {!collapsed && label && (
        <div className="flex items-center h-7 px-2 mb-0.5 text-xs font-medium text-muted">Agents</div>
      )}
      {rows.map((r) => (
        <AgentLine key={r.id} run={r} collapsed={collapsed} onPick={onPick} />
      ))}
    </div>
  )
}
